import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, inr } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCart } from "@/context/cart";
import { toast } from "sonner";
import { ArrowLeft, ShoppingCart } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (url) => (!url ? "" : url.startsWith("/api/") ? `${BACKEND}${url}` : url);

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      setVariant(r.data.variants?.[0] || null);
    });
  }, [id]);

  if (!p) return <div className="min-h-screen bg-white"><Navbar/><div className="p-12 text-sm text-gray-500">Loading…</div></div>;

  const price = variant?.price ?? p.base_price;

  const onAdd = () => {
    if (!variant) { toast.error("Select a variant"); return; }
    add({
      product_id: p.id,
      product_name: p.name,
      variant_name: variant.name,
      price,
      quantity: qty,
      notes,
      image_url: p.image_url,
    });
    toast.success("Added to cart");
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 md:px-8 py-10">
        <button onClick={() => nav(-1)} className="text-sm text-gray-500 hover:text-gray-900 mb-6 inline-flex items-center gap-1" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5}/> Back
        </button>
        <div className="grid md:grid-cols-2 gap-10">
          <div className="aspect-square bg-gray-50 border border-gray-200 overflow-hidden">
            {p.image_url && <img src={resolveImg(p.image_url)} alt={p.name} className="w-full h-full object-cover"/>}
          </div>
          <div>
            <h1 className="text-3xl font-heading font-semibold tracking-tight" data-testid="product-title">{p.name}</h1>
            <p className="text-sm text-gray-600 mt-3 leading-relaxed">{p.description}</p>
            <div className="mt-6 text-2xl font-heading font-semibold text-[#002fa7]" data-testid="product-price">{inr(price)}</div>

            <div className="mt-6">
              <Label className="text-xs font-medium uppercase tracking-wider text-gray-600">Variant</Label>
              <div className="grid grid-cols-1 gap-2 mt-2">
                {(p.variants || []).map((v) => (
                  <button
                    key={v.name}
                    onClick={() => setVariant(v)}
                    data-testid={`variant-${v.name.replace(/\s+/g, "-")}`}
                    className={`text-left border p-3 transition-colors ${variant?.name === v.name ? "border-[#002fa7] bg-blue-50" : "border-gray-200 hover:border-gray-400"}`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{v.name}</span>
                      <span className="text-sm text-gray-700">{inr(v.price)}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">{v.stock} in stock</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium uppercase tracking-wider text-gray-600">Quantity</Label>
                <Input type="number" min={1} value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))} className="rounded-sm mt-1" data-testid="qty-input"/>
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-xs font-medium uppercase tracking-wider text-gray-600">Customisation notes</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g., specific colours, logo placement, special instructions" className="rounded-sm mt-1" data-testid="notes-input"/>
            </div>

            <Button onClick={onAdd} data-testid="add-to-cart-btn" className="mt-6 w-full bg-[#002fa7] hover:bg-[#00227b] rounded-sm h-12">
              <ShoppingCart className="w-4 h-4 mr-2" strokeWidth={1.5}/> Add to cart — {inr(price * qty)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
